import { APP_INITIALIZER, Provider, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';
import { RuntimeConfigurationService } from '../services/runtime-configuration.service';
import { LoggingService } from '../services/logging.service';

/**
 * Application Configuration Initializer
 *
 * This initializer ensures that runtime configuration is loaded
 * before the Angular application starts. This is critical for
 * security as it prevents the app from running with potentially
 * insecure fallback configurations.
 */
export function initializeAppConfiguration(
  configService: RuntimeConfigurationService,
  loggingService: LoggingService,
  _router: Router,
  platformId: object,
): () => Promise<boolean> {
  return async (): Promise<boolean> => {
    const isBrowser = isPlatformBrowser(platformId);

    try {
      const startTime = Date.now();
      const configLoaded = await configService.initialize();
      const loadTime = Date.now() - startTime;

      if (configLoaded) {

        // Validate critical configuration
        const config = configService.getCurrentConfiguration();
        if (config) {

          const validationResult = validateConfiguration(config);
          if (!validationResult.isValid) {
            loggingService.logError('❌ Configuration validation failed', {
              errors: validationResult.errors,
            });
            // Defer redirect: set bootstrap error flag for guards to handle
            if (isBrowser) {
              sessionStorage.setItem('bootstrapError', 'config-validation-failed');
            }
            return true; // allow app to render; guards will redirect to /error
          }
        }

        // Configuration loaded and validated successfully - clear any previous bootstrap error
        if (isBrowser) {
          sessionStorage.removeItem('bootstrapError');
        }
        return true;
      } else {
        loggingService.logError('❌ Failed to load application configuration', {
          loadTimeMs: loadTime,
          fallbackUsed: configService.getCurrentConfiguration() !== null,
        });
        // Fail-hard: defer redirect to /error; router may not be ready here
        if (isBrowser) {
          sessionStorage.setItem('bootstrapError', 'config-load-failed');
        }
        return true; // allow app to render; guards will redirect to /error
      }
    } catch (error) {
      loggingService.logError('❌ Configuration initialization failed', {
        error: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined,
      });
      // Defer redirect to /error; guards will handle after router is ready
      if (isBrowser) {
        sessionStorage.setItem('bootstrapError', 'config-initializer-exception');
      }
      return true; // allow app to render; guards will redirect to /error
    }
  };
}

/**
 * Validate loaded configuration for security and completeness
 */
function validateConfiguration(config: any): {
  isValid: boolean;
  errors: string[];
} {
  const errors: string[] = [];

  // Validate required services
  if (!config.services) {
    errors.push('Missing services configuration');
  } else {
    const requiredServices = ['baseFunction', 'alertsFunction', 'emailService'];
    for (const service of requiredServices) {
      if (!config.services[service]) {
        errors.push(`Missing required service: ${service}`);
      }
    }
  }

  // Validate security configuration
  if (!config.security) {
    errors.push('Missing security configuration');
  } else {
    if (
      !config.security.sessionTimeout ||
      config.security.sessionTimeout < 5 * 60 * 1000
    ) {
      errors.push('Invalid session timeout configuration');
    }
    if (
      !config.security.maxFailedAttempts ||
      config.security.maxFailedAttempts < 1
    ) {
      errors.push('Invalid failed attempts configuration');
    }
  }

  // Validate feature flags
  if (!config.features) {
    errors.push('Missing feature flags configuration');
  }

  // Validate CSP if security is enabled
  if (config.security?.enableCSP && !config.csp) {
    errors.push('CSP enabled but no CSP configuration found');
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
}

/**
 * Provider for APP_INITIALIZER
 */
export const APP_CONFIG_INITIALIZER: Provider = {
  provide: APP_INITIALIZER,
  useFactory: initializeAppConfiguration,
  deps: [RuntimeConfigurationService, LoggingService, Router, PLATFORM_ID],
  multi: true,
};
